/**
 * "Claude wants to connect to Dantria." The one screen in the OAuth flow a
 * person actually reads.
 *
 * It is a page in the SPA rather than HTML built in the Worker for two
 * reasons. It needs a session — the whole question is *who* is approving — and
 * the app already knows how to get one, so an unauthenticated visitor lands on
 * the ordinary sign-in screen and comes back here afterwards. And it needs to
 * look like the rest of the product: a consent screen is exactly where someone
 * decides whether to trust what they are looking at, and a page that looked
 * improvised would be the wrong answer to that question.
 *
 * Two decisions are made here and nowhere else, because only a person can make
 * them: which company the assistant may act on, and whether it may approve
 * contracts. A client cannot ask for either and get it — `negotiateScope` on
 * the server refuses to grant `mcp:approve` unless this checkbox says so.
 *
 * Nothing redirects until Approve is pressed. Every parameter is validated by
 * the server first, so a malformed or hostile request produces an error on
 * this page rather than bouncing the user somewhere on the strength of an
 * unchecked redirect URI.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { SignIn } from "./SignIn";
import { Button } from "./ui";

interface ConsentContext {
  client: { name: string; uri: string | null; logoUri: string | null };
  companies: { id: string; slug: string; name: string }[];
  requestedScope: string;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
        {children}
      </div>
    </div>
  );
}

export function OAuthConsent() {
  const params = new URLSearchParams(window.location.search);
  const query = window.location.search;

  const [companyId, setCompanyId] = useState("");
  const [allowApprove, setAllowApprove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ctx = useQuery({
    queryKey: ["oauth-consent", query],
    queryFn: async (): Promise<ConsentContext> => {
      const res = await fetch(`/api/oauth/authorize/context${query}`);
      if (res.status === 401) throw new Error("__signin__");
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        error_description?: string;
      };
      if (!res.ok)
        throw new Error(body.error_description ?? body.error ?? "That request isn't valid.");
      return body as unknown as ConsentContext;
    },
    retry: false,
  });

  const approve = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/oauth/authorize", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          clientId: params.get("client_id"),
          redirectUri: params.get("redirect_uri"),
          codeChallenge: params.get("code_challenge"),
          state: params.get("state") ?? undefined,
          scope: params.get("scope") ?? undefined,
          resource: params.get("resource") ?? undefined,
          companyId,
          allowApprove,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        redirectTo?: string;
        error?: string;
        error_description?: string;
      };
      if (!res.ok || !body.redirectTo) {
        throw new Error(body.error_description ?? body.error ?? "Couldn't complete that.");
      }
      return body.redirectTo;
    },
    onSuccess: (redirectTo) => {
      // `replace`, not `assign`: the consent URL carries a single-use
      // challenge, and leaving it in history invites a back-button retry that
      // can only fail confusingly.
      window.location.replace(redirectTo);
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  /**
   * Denial is a redirect too, not a dead end — RFC 6749 says an unwilling user
   * is reported back to the client as `access_denied`, which is how Claude
   * shows "you cancelled" instead of hanging on a window that never returns.
   */
  function deny() {
    const redirect = params.get("redirect_uri");
    if (!redirect) {
      window.location.href = "/";
      return;
    }
    const url = new URL(redirect);
    url.searchParams.set("error", "access_denied");
    const state = params.get("state");
    if (state) url.searchParams.set("state", state);
    window.location.replace(url.toString());
  }

  if (ctx.isPending) {
    return <Shell>Loading…</Shell>;
  }

  // Not signed in: the ordinary sign-in screen, which on success re-runs this
  // query and lands the user right back on the consent step they asked for.
  if (ctx.isError && (ctx.error as Error).message === "__signin__") {
    return <SignIn onSignedIn={() => ctx.refetch()} />;
  }

  if (ctx.isError) {
    return (
      <Shell>
        <h1 className="mb-2 font-[family-name:--font-display] font-semibold text-lg text-neutral-900">
          That connection request isn't valid
        </h1>
        <p className="text-neutral-600 text-sm">{(ctx.error as Error).message}</p>
        <p className="mt-4 text-neutral-500 text-sm">
          Try connecting again from the app that sent you here.
        </p>
      </Shell>
    );
  }

  const data = ctx.data;

  // Signed in, but not an owner anywhere. Handing out standing access to a
  // company's books is an ownership decision, the same rule that gates issuing
  // a key by hand — so say that plainly rather than showing an empty dropdown.
  if (data.companies.length === 0) {
    return (
      <Shell>
        <h1 className="mb-2 font-[family-name:--font-display] font-semibold text-lg text-neutral-900">
          You can't connect an assistant
        </h1>
        <p className="text-neutral-600 text-sm">
          Only an owner can give an assistant standing access to a company's books. Ask an owner to
          connect {data.client.name}, or to make you an owner.
        </p>
      </Shell>
    );
  }

  const selected = companyId || data.companies[0]?.id || "";

  return (
    <Shell>
      <div className="mb-5 flex items-center gap-3">
        {data.client.logoUri && (
          <img
            src={data.client.logoUri}
            alt=""
            className="size-10 rounded-lg border border-neutral-200 object-contain"
          />
        )}
        <div>
          <h1 className="font-[family-name:--font-display] font-semibold text-lg text-neutral-900">
            Connect {data.client.name}
          </h1>
          <p className="text-neutral-500 text-sm">to your Dantria books</p>
        </div>
      </div>

      <p className="mb-4 text-neutral-600 text-sm">
        It will be able to read your price book, price work against it, add clients, and create
        proposals and contracts. It can never delete anything.
      </p>

      <label className="mb-1 block font-medium text-neutral-900 text-sm" htmlFor="oauth-company">
        Which company?
      </label>
      <select
        id="oauth-company"
        className="mb-4 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm"
        value={selected}
        onChange={(e) => setCompanyId(e.target.value)}
      >
        {data.companies.map((company) => (
          <option key={company.id} value={company.id}>
            {company.name}
          </option>
        ))}
      </select>

      <label className="mb-1 flex items-start gap-2 text-neutral-700 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={allowApprove}
          onChange={(e) => setAllowApprove(e.target.checked)}
        />
        <span>Also let it approve contracts</span>
      </label>
      <p className="mb-5 pl-6 text-neutral-500 text-sm">
        Approving turns a signed contract into live projects, milestones and commitments, and posts
        to your books. Leave this off unless you mean it.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={deny} disabled={approve.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            setCompanyId(selected);
            approve.mutate();
          }}
          disabled={approve.isPending || !selected}
        >
          {approve.isPending ? "Connecting…" : "Approve"}
        </Button>
      </div>
    </Shell>
  );
}
